import React, { useEffect } from "react";
import "./adminDashboard.css";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from "@mui/material";
import {
  People,
  Home,
  Person,
  Business,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Pending,
  ArrowForward,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS, userCan } from "../../../config/adminRoles";

const StatCard = ({ title, value, icon, color, trend, trendValue, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 3,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      "&:hover": onClick ? {
        transform: "translateY(-4px)",
        boxShadow: `0 12px 24px ${color}20`,
      } : {},
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color: color }}>
            {value || 0}
          </Typography>
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              {trend === "up" ? (
                <TrendingUp sx={{ color: "#22c55e", fontSize: 18, mr: 0.5 }} />
              ) : (
                <TrendingDown sx={{ color: "#ef4444", fontSize: 18, mr: 0.5 }} />
              )}
              <Typography variant="caption" sx={{ color: trend === "up" ? "#22c55e" : "#ef4444" }}>
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}20`,
            width: 56,
            height: 56,
          }}
        >
          {React.cloneElement(icon, { sx: { color: color, fontSize: 28 } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const QuickActionCard = ({ title, description, icon, color, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 2.5,
      borderRadius: 2,
      cursor: "pointer",
      border: "1px solid #e5e7eb",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: color,
        boxShadow: `0 4px 12px ${color}15`,
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar sx={{ bgcolor: `${color}15`, width: 44, height: 44 }}>
        {React.cloneElement(icon, { sx: { color: color, fontSize: 22 } })}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <ArrowForward sx={{ color: "#9ca3af", fontSize: 20 }} />
    </Box>
  </Paper>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, GetAdminDetail } = useSelector((state) => state.getAdminDetail);
  const stats = GetAdminDetail;
  const currentUser = useSelector((state) => state.validateUser?.UserValidate?.data?.user);
  // Each block below is shown only to an admin whose role opens the page it links to
  const can = (permission) => userCan(currentUser, permission);
  const hasQuickActions = [
    PERMISSIONS.VIEW_LANDLORDS,
    PERMISSIONS.VIEW_PROPERTIES,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.VIEW_REVENUE,
  ].some(can);

  useEffect(() => {
    dispatch(GetAdmin());
  }, [dispatch]);

  // Calculate pending counts
  const pendingLandlords = can(PERMISSIONS.VIEW_LANDLORDS)
    ? stats?.landlord_detail?.filter(
        (l) => l.is_register === true && l.is_active === false
      )?.length || 0
    : 0;

  const pendingProperties = can(PERMISSIONS.VIEW_PROPERTIES)
    ? stats?.property_detail?.reduce((count, property) => {
        const pendingFlats = property.flats?.filter((f) => !f.isApproved)?.length || 0;
        return count + pendingFlats;
      }, 0) || 0
    : 0;

  // Recent landlords (last 5)
  const recentLandlords = stats?.landlord_detail?.slice(-5)?.reverse() || [];

  // Recent properties (last 5)
  const recentProperties = stats?.property_detail?.slice(-5)?.reverse() || [];

  // Loading Skeleton Component
  const DashboardSkeleton = () => (
    <>
      {/* Header Skeleton */}
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={200} height={45} />
        <Skeleton variant="text" width={300} height={24} />
      </Box>

      {/* Stats Cards Skeleton */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 3, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Skeleton variant="circular" width={56} height={56} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={60} height={36} />
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tables Skeleton */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
                <Skeleton variant="rounded" width={60} height={24} />
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Skeleton variant="rounded" width={50} height={50} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
                <Skeleton variant="rounded" width={70} height={24} />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  return (
    <>
      <AdminWrapper />
      <Box
        className={`admin-main-content ${open ? "sidebar-open" : "sidebar-closed"}`}
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? (
          <DashboardSkeleton />
        ) : (
        <>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back! Here's what's happening with your platform.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {can(PERMISSIONS.VIEW_USERS) && (
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Total Users"
                value={stats?.user_count}
                icon={<People />}
                color="#6366f1"
                trend="up"
                trendValue="+12% this month"
                onClick={() => navigate("/admin/users")}
              />
            </Grid>
          )}
          {can(PERMISSIONS.VIEW_LANDLORDS) && (
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Landlords"
                value={stats?.landlord_count}
                icon={<Person />}
                color="#22c55e"
                onClick={() => navigate("/admin/landlords")}
              />
            </Grid>
          )}
          {can(PERMISSIONS.VIEW_TENANTS) && (
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Tenants"
                value={stats?.tenant_count}
                icon={<Home />}
                color="#f59e0b"
                onClick={() => navigate("/admin/tenants")}
              />
            </Grid>
          )}
          {can(PERMISSIONS.VIEW_PROPERTIES) && (
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Properties"
                value={stats?.property_count}
                icon={<Business />}
                color="#e94560"
                onClick={() => navigate("/admin/properties")}
              />
            </Grid>
          )}
        </Grid>

        {/* Pending Approvals Alert */}
        {(pendingLandlords > 0 || pendingProperties > 0) && (
          <Paper
            sx={{
              p: 2.5,
              mb: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, #fef3c7 0%, #fef9c3 100%)",
              border: "1px solid #fcd34d",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Pending sx={{ color: "#d97706" }} />
              <Typography sx={{ flex: 1, fontWeight: 500, color: "#92400e" }}>
                You have pending approvals:
                {pendingLandlords > 0 && (
                  <Chip
                    label={`${pendingLandlords} Landlord${pendingLandlords > 1 ? "s" : ""}`}
                    size="small"
                    sx={{ ml: 1, bgcolor: "#fbbf24", color: "#78350f" }}
                  />
                )}
                {pendingProperties > 0 && (
                  <Chip
                    label={`${pendingProperties} Propert${pendingProperties > 1 ? "ies" : "y"}`}
                    size="small"
                    sx={{ ml: 1, bgcolor: "#fbbf24", color: "#78350f" }}
                  />
                )}
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" } }}
                onClick={() => navigate(pendingLandlords > 0 ? "/admin/landlords" : "/admin/properties")}
              >
                Review Now
              </Button>
            </Box>
          </Paper>
        )}

        {/* Quick Actions & Recent Activity */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Quick Actions */}
          {hasQuickActions && (
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2.5 }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {can(PERMISSIONS.VIEW_LANDLORDS) && (
                    <QuickActionCard
                      title="Manage Landlords"
                      description="Review & approve landlords"
                      icon={<Person />}
                      color="#22c55e"
                      onClick={() => navigate("/admin/landlords")}
                    />
                  )}
                  {can(PERMISSIONS.VIEW_PROPERTIES) && (
                    <QuickActionCard
                      title="Manage Properties"
                      description="Review property listings"
                      icon={<Business />}
                      color="#e94560"
                      onClick={() => navigate("/admin/properties")}
                    />
                  )}
                  {can(PERMISSIONS.VIEW_TENANTS) && (
                    <QuickActionCard
                      title="View Tenants"
                      description="Browse all tenants"
                      icon={<Home />}
                      color="#f59e0b"
                      onClick={() => navigate("/admin/tenants")}
                    />
                  )}
                  {can(PERMISSIONS.VIEW_REVENUE) && (
                    <QuickActionCard
                      title="Revenue Reports"
                      description="View earnings & analytics"
                      icon={<AttachMoney />}
                      color="#6366f1"
                      onClick={() => navigate("/admin/revenue")}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Recent Landlords */}
          {can(PERMISSIONS.VIEW_LANDLORDS) && (
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Landlords
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate("/admin/landlords")}
                  >
                    View All
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentLandlords.length > 0 ? (
                        recentLandlords.map((landlord, index) => (
                          <TableRow key={landlord._id || index} hover>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: "#22c55e20", color: "#22c55e" }}>
                                  {(landlord.name || "L").charAt(0).toUpperCase()}
                                </Avatar>
                                {landlord.name || "N/A"}
                              </Box>
                            </TableCell>
                            <TableCell>{landlord.email || "N/A"}</TableCell>
                            <TableCell>{landlord.contact || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={landlord.is_active ? "Active" : "Pending"}
                                sx={{
                                  bgcolor: landlord.is_active ? "#dcfce7" : "#fef3c7",
                                  color: landlord.is_active ? "#166534" : "#92400e",
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => navigate("/admin/landlords")}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No landlords found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Recent Properties */}
        {can(PERMISSIONS.VIEW_PROPERTIES) && (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Properties
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate("/admin/properties")}
              >
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Flats</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentProperties.length > 0 ? (
                    recentProperties.map((property, index) => (
                      <TableRow key={property._id || index} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#e9456020", color: "#e94560" }}>
                              <Business fontSize="small" />
                            </Avatar>
                            {property.owner_name || property.email || "N/A"}
                          </Box>
                        </TableCell>
                        <TableCell>{property.address || "N/A"}</TableCell>
                        <TableCell>{property.city || "N/A"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`${property.flats?.length || 0} Flat${property.flats?.length !== 1 ? "s" : ""}`}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {property.flats?.some((f) => !f.isApproved) ? (
                            <Chip
                              size="small"
                              label="Pending Review"
                              sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 500 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Approved"
                              sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 500 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => navigate("/admin/properties")}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No properties found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        </>
        )}
      </Box>
    </>
  );
};

export default AdminDashboard;
